import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Card, 
  CardContent, 
  useTheme, 
  alpha, 
  Grid,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { 
  AreaChart, 
  Area, 
  LineChart,
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  ReferenceArea,
  BarChart,
  Bar
} from 'recharts';

// Icons
import AccessTimeIcon from '@mui/icons-material/AccessTime';  // Hourly
import TodayIcon from '@mui/icons-material/Today';  // Daily
import ViewWeekIcon from '@mui/icons-material/ViewWeek';  // Weekly
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';  // Monthly
import DateRangeIcon from '@mui/icons-material/DateRange';  // Last Week
import EventIcon from '@mui/icons-material/Event';  // Last Month
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';  // Last Year
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';  // All Time

// Frequency type
type FrequencyType = 'Hourly' | 'Daily' | 'Weekly' | 'Monthly';

// Time frame type
type TimeFrameType = 'Last Week' | 'Last 30 Days' | 'Last Year' | 'All Time';

// Type definitions for data structures
interface OperationalHours {
  schedule: {
    [key: string]: {
      day: string;
      start: string | null;
      end: string | null;
      isOpen: boolean;
    };
  };
  timezone: string;
}

interface TimeSeriesDataPoint {
  timestamp: string;
  kwh: number;
}

interface YearlyDataPoint {
  month: string;
  year: number;
  kwh: number;
}

interface YearComparisonDataPoint {
  month: string;
  currentYear: number;
  currentYearLabel: string;
  previousYear: number;
  previousYearLabel: string;
}

interface AllTimeDataPoint {
  id: string;
  month: string;
  year: number;
  kwh: number;
  label: string;
}

interface EnergyInsightsData {
  // New keys for direct usage
  last_week_hourly: TimeSeriesDataPoint[];
  last_week_daily: TimeSeriesDataPoint[];
  last_30_days_hourly: TimeSeriesDataPoint[];
  last_30_days_daily: TimeSeriesDataPoint[];
  last_30_days_weekly: TimeSeriesDataPoint[];
  last_year_weekly: TimeSeriesDataPoint[];
  last_year_monthly: {
    current_year: YearlyDataPoint[];
    previous_year: YearlyDataPoint[];
  };
  all_time_monthly: YearlyDataPoint[];
  
  // Legacy keys for backward compatibility
  daily_latest: TimeSeriesDataPoint[];
  weekly_latest_hourly: TimeSeriesDataPoint[];
  monthly_latest_daily: TimeSeriesDataPoint[];
  yearly_latest_monthly: {
    current_year: YearlyDataPoint[];
    previous_year: YearlyDataPoint[];
  };
  hourly_last_7_days: TimeSeriesDataPoint[];
  hourly_last_30_days: TimeSeriesDataPoint[];
  
  peakTimesLatestMonth: any[];
  operationalUsage: {
    month: string;
    operational: number;
    nonOperational: number;
  };
  periodCovered: {
    start: string;
    end: string;
    latestDay: string;
    latestWeek: {
      start: string;
      end: string;
    };
    latestMonth: string;
    latestYear: string;
  };
}

interface ReferenceAreaData {
  x1: string;
  x2: string;
  day?: string;
}

// Custom interface for bar chart data with year property
interface MonthlyBarChartDataPoint {
  month: string;
  year: number;
  kwh: number;
  previousYear: number;
  timestamp: string;
  label: string;
}

const UsagePatterns = () => {
  const theme = useTheme();
  
  // States for selectors
  const [timeFrame, setTimeFrame] = useState<TimeFrameType>('Last Week');
  const [frequency, setFrequency] = useState<FrequencyType>('Hourly');
  
  // Data states
  const [energyInsights, setEnergyInsights] = useState<EnergyInsightsData | null>(null);
  const [operationalHours, setOperationalHours] = useState<OperationalHours | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get available frequency options based on selected timeframe
  const getAvailableFrequencies = (selectedTimeFrame: TimeFrameType): FrequencyType[] => {
    switch(selectedTimeFrame) {
      case 'Last Week':
        return ['Hourly', 'Daily'];
      case 'Last 30 Days':
        return ['Hourly', 'Daily', 'Weekly'];
      case 'Last Year':
        return ['Weekly', 'Monthly'];
      case 'All Time':
        return ['Monthly'];
      default:
        return ['Hourly']; // Fallback
    }
  };
  
  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch energy insights data
        const insightsResponse = await fetch('/data/energy_insights_BIZ001.json');
        if (!insightsResponse.ok) {
          throw new Error('Failed to fetch energy insights data');
        }
        const insightsData = await insightsResponse.json();
        setEnergyInsights(insightsData);
        
        // Fetch operational hours data
        const hoursResponse = await fetch('/data/operational_hours.json');
        if (!hoursResponse.ok) {
          throw new Error('Failed to fetch operational hours data');
        }
        const hoursData = await hoursResponse.json();
        setOperationalHours(hoursData);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // When timeFrame changes, update frequency if current selection is not available
  useEffect(() => {
    // Get available frequencies for the new timeFrame
    const availableFrequencies = getAvailableFrequencies(timeFrame);
    
    // Check if current frequency is valid for the new timeFrame
    if (!availableFrequencies.includes(frequency)) {
      // If not, set to the first available frequency
      setFrequency(availableFrequencies[0]);
    }
  }, [timeFrame]);
  
  // Handle time frame change
  const handleTimeFrameChange = (event: SelectChangeEvent<TimeFrameType>) => {
    const newTimeFrame = event.target.value as TimeFrameType;
    setTimeFrame(newTimeFrame);
  };
  
  // Handle frequency change
  const handleFrequencyChange = (event: SelectChangeEvent<FrequencyType>) => {
    const newFrequency = event.target.value as FrequencyType;
    setFrequency(newFrequency);
  };
  
  // Format date for X-axis based on frequency
  const formatXAxis = (value: string) => {
    if (!value) return '';
    
    try {
      const date = new Date(value);
      
      switch (frequency) {
        case 'Hourly':
          return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        case 'Daily':
          return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        case 'Weekly':
          // Format: "Week of MMM DD"
          return `Week of ${date.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
        case 'Monthly':
          return date.toLocaleDateString([], { month: 'short', year: 'numeric' });
        default:
          return value;
      }
    } catch (err) {
      console.error('Error formatting X-axis value:', err);
      return value;
    }
  };
  
  // Helper function to get week number from date
  const getWeekNumber = (date: Date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };
  
  // Build a key to access the data based on timeFrame and frequency
  const getDataKey = (): string => {
    // Special case for last_year_monthly which has a different structure
    if (timeFrame === 'Last Year' && frequency === 'Monthly') {
      return 'last_year_monthly';
    }
    
    // For standard keys in format last_[timeframe]_[frequency] (all lowercase)
    const timeFrameKey = timeFrame.toLowerCase().replace(/\s+/g, '_');
    const frequencyKey = frequency.toLowerCase();
    
    return `${timeFrameKey}_${frequencyKey}`;
  };
  
  // Get chart data based on frequency and time frame
  const getChartData = () => {
    if (!energyInsights) return [];
    
    const dataKey = getDataKey();
    
    // Special case for last_year_monthly which has a different structure
    if (dataKey === 'last_year_monthly') {
      const currentYearData = energyInsights.last_year_monthly.current_year || [];
      const previousYearData = energyInsights.last_year_monthly.previous_year || [];
      
      // Merge the current and previous year data
      const combinedData = currentYearData.map(item => {
        const prevYearItem = previousYearData.find(prev => prev.month === item.month);
        
        return {
          month: item.month,
          year: item.year,
          kwh: item.kwh,
          previousYear: prevYearItem ? prevYearItem.kwh : 0,
          previousYearLabel: prevYearItem ? `${prevYearItem.year}` : '',
          currentYearLabel: `${item.year}`,
          label: item.month
        };
      });
      
      // Sort by month
      return combinedData.sort((a, b) => getMonthNumber(a.month) - getMonthNumber(b.month));
    }
    
    // Standard case - access the data directly using the constructed key
    const data = (energyInsights as any)[dataKey] || [];
    
    // For all_time_monthly, enhance the data with label
    if (dataKey === 'all_time_monthly') {
      return data.map((item: YearlyDataPoint) => ({
        ...item,
        id: `${item.month}-${item.year}`,
        label: `${item.month} ${item.year}`
      }));
    }
    
    return data;
  };
  
  // Helper function to get month number from abbreviation
  const getMonthNumber = (monthAbbr: string) => {
    const months = {
      'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
      'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
    };
    return months[monthAbbr as keyof typeof months] || 1;
  };
  
  // Get chart title based on frequency and time frame
  const getChartTitle = () => {
    if (!energyInsights) return '';
    
    return `${frequency} Energy Usage (${timeFrame})`;
  };
  
  // Get operational hours reference areas for highlighting on chart
  const getOperationalHoursAreas = (): ReferenceAreaData[] => {
    if (!operationalHours || !energyInsights) return [];
    
    // Only apply to hourly frequency
    if (frequency !== 'Hourly') {
      return [];
    }
    
    const referenceAreas: ReferenceAreaData[] = [];
    
    // Get chart data to determine the date range
    const data = getChartData();
    if (!data.length) return [];
    
    // Group by date to find unique days in the filtered data
    const uniqueDates = new Map<string, { day: number, dateStr: string }>();
    
    data.forEach((point: TimeSeriesDataPoint) => {
      const date = new Date(point.timestamp);
      const dateStr = date.toISOString().split('T')[0];
      
      if (!uniqueDates.has(dateStr)) {
        uniqueDates.set(dateStr, {
          day: date.getDay(),
          dateStr
        });
      }
    });
    
    // Create reference areas for each date based on operational hours
    uniqueDates.forEach(({ day, dateStr }) => {
      const dayStr = day.toString();
      const schedule = operationalHours.schedule[dayStr];
      
      if (schedule.isOpen && schedule.start && schedule.end) {
        referenceAreas.push({
          x1: `${dateStr}T${schedule.start}:00`,
          x2: `${dateStr}T${schedule.end}:00`,
          day: schedule.day
        });
      }
    });
    
    return referenceAreas;
  };
  
  // Render the appropriate chart based on frequency and time frame
  const renderChart = () => {
    if (!energyInsights || loading) return null;
    
    const data = getChartData();
    
    if (!data.length) {
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: 400,
          flexDirection: 'column',
          bgcolor: alpha(theme.palette.grey[200], 0.3),
          borderRadius: 2
        }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>No data available</Typography>
          <Typography variant="body2" color="text.secondary">
            Try selecting a different frequency or time frame
          </Typography>
        </Box>
      );
    }
    
    const referenceAreas = getOperationalHoursAreas();
    const showOperationalHours = frequency === 'Hourly';
    const dataKey = getDataKey();
    const chartTitle = getChartTitle();
    
    // Common function to render the legend with operational hours if needed
    const renderLegend = (mainLabel: string) => (
      <Legend 
        payload={showOperationalHours ? [
          { value: mainLabel, type: 'line', color: theme.palette.primary.main },
          { 
            value: 'Green Highlighted Areas = Operational Hours', 
            type: 'rect', 
            color: alpha(theme.palette.success.main, 0.4), 
            id: 'opHours' 
          }
        ] : [
          { value: mainLabel, type: 'line', color: theme.palette.primary.main }
        ]}
        verticalAlign={showOperationalHours ? "top" : "bottom"}
        align={showOperationalHours ? "right" : "center"}
        wrapperStyle={showOperationalHours ? { 
          paddingBottom: 10,
          fontSize: '0.85rem',
          fontWeight: 'bold'
        } : undefined}
        iconSize={10}
        formatter={(value, entry) => {
          // Make operational hours text green and bold
          if (entry.id === 'opHours') {
            return <span style={{ color: theme.palette.success.main, fontWeight: 'bold' }}>{value}</span>;
          }
          return <span>{value}</span>;
        }}
      />
    );
    
    // For monthly or weekly data in Last Year timeframe
    if (dataKey === 'last_year_monthly' || dataKey === 'last_year_weekly') {
      return (
        <>
          <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'medium' }}>
            {chartTitle}
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart
              data={dataKey === 'last_year_monthly' ? data : data}
              margin={{ top: 10, right: 30, left: 20, bottom: 40 }}
            >
              <defs>
                <linearGradient id="colorLastYear" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.1)} />
              <XAxis 
                dataKey={dataKey === 'last_year_monthly' ? "label" : "timestamp"} 
                tick={{ fontSize: 12 }}
                height={40}
                tickFormatter={dataKey === 'last_year_weekly' ? formatXAxis : undefined}
              />
              <YAxis 
                label={{ 
                  value: 'Energy (kWh)', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }} 
              />
              <Tooltip 
                formatter={(value: number, name: string) => {
                  // For monthly data, we might still have previous year data in the object
                  if (name === 'kwh' || name === 'previousYear') {
                    return [`${value.toLocaleString()} kWh`, name === 'kwh' ? 'Current Year' : 'Previous Year'];
                  }
                  return [`${value.toLocaleString()} kWh`, 'Energy Usage'];
                }}
                labelFormatter={(label) => {
                  if (dataKey === 'last_year_monthly') {
                    // For monthly data, the label is just the month name
                    return `${label} ${data[0]?.year || ''}`;
                  }
                  
                  // For weekly data, format the timestamp
                  try {
                    const date = new Date(label);
                    const endOfWeek = new Date(date);
                    endOfWeek.setDate(date.getDate() + 6);
                    return `Week of ${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`;
                  } catch (err) {
                    return label;
                  }
                }}
              />
              {renderLegend(dataKey === 'last_year_monthly' ? 'Current Year' : 'Energy Usage')}
              
              {/* Operational hours highlighting - only for hourly frequency */}
              {showOperationalHours && (
                <>
                  {/* Render reference areas for operational hours */}
                  {referenceAreas.map((area, index) => (
                    <ReferenceArea
                      key={`op-hours-${index}`}
                      x1={area.x1}
                      x2={area.x2}
                      fill={alpha(theme.palette.success.main, 0.4)}
                      fillOpacity={0.7}
                    />
                  ))}
                </>
              )}
              
              {dataKey === 'last_year_monthly' ? (
                <>
                  <Area 
                    type="monotone" 
                    dataKey="kwh" 
                    name="Current Year"
                    stroke={theme.palette.primary.main} 
                    fillOpacity={1}
                    fill="url(#colorLastYear)" 
                    activeDot={{ r: 8 }}
                    strokeWidth={2}
                  />
                </>
              ) : (
                <Area 
                  type="monotone" 
                  dataKey="kwh" 
                  name="Energy Usage"
                  stroke={theme.palette.primary.main} 
                  fillOpacity={1}
                  fill="url(#colorLastYear)" 
                  activeDot={{ r: 8 }}
                  strokeWidth={2}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </>
      );
    }
    
    // For all time monthly data - now with area fill
    if (dataKey === 'all_time_monthly') {
      return (
        <>
          <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'medium' }}>
            {chartTitle}
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart
              data={data}
              margin={{ top: 10, right: 30, left: 20, bottom: 60 }}
            >
              <defs>
                <linearGradient id="colorAllTime" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.1)} />
              <XAxis 
                dataKey="label" 
                tick={{ fontSize: 10, textAnchor: 'end' }}
                height={80}
                interval={0}
                angle={-45}
              />
              <YAxis 
                label={{ 
                  value: 'Energy (kWh)', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }} 
              />
              <Tooltip 
                formatter={(value: number) => [`${value.toLocaleString()} kWh`, 'Energy Usage']}
                labelFormatter={(label) => label}
              />
              {renderLegend('Energy Usage')}
              
              {/* Operational hours highlighting - only for hourly frequency */}
              {showOperationalHours && (
                <>
                  {/* Render reference areas for operational hours */}
                  {referenceAreas.map((area, index) => (
                    <ReferenceArea
                      key={`op-hours-${index}`}
                      x1={area.x1}
                      x2={area.x2}
                      fill={alpha(theme.palette.success.main, 0.4)}
                      fillOpacity={0.7}
                    />
                  ))}
                </>
              )}
              
              <Area 
                type="monotone" 
                dataKey="kwh" 
                name="Energy Usage"
                stroke={theme.palette.primary.main} 
                fillOpacity={1}
                fill="url(#colorAllTime)" 
                strokeWidth={2}
                activeDot={{ r: 6 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </>
      );
    }
    
    // For all other time series data
    return (
      <>
        <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'medium' }}>
          {chartTitle}
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart
            data={data}
            margin={{ top: 10, right: 30, left: 20, bottom: 30 }}
          >
            <defs>
              <linearGradient id="colorEnergy" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.1)} />
            <XAxis 
              dataKey="timestamp" 
              tick={{ fontSize: 12 }}
              tickFormatter={formatXAxis}
              height={frequency === 'Weekly' ? 70 : 40}
              label={{ 
                value: frequency === 'Hourly' ? 'Time' : 
                       frequency === 'Daily' ? 'Date' : 
                       frequency === 'Weekly' ? 'Week' : 'Month',
                position: 'insideBottomRight',
                offset: -10
              }}
            />
            <YAxis 
              label={{ 
                value: 'Energy (kWh)', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle' }
              }} 
            />
            <Tooltip 
              formatter={(value: number) => [`${value.toLocaleString()} kWh`, 'Energy Usage']}
              labelFormatter={(label) => {
                try {
                  const date = new Date(label);
                  if (frequency === 'Hourly') {
                    return `${date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                  } else if (frequency === 'Daily') {
                    return `${date.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}`;
                  } else if (frequency === 'Weekly') {
                    const endOfWeek = new Date(date);
                    endOfWeek.setDate(date.getDate() + 6);
                    return `Week of ${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`;
                  } else {
                    return `${date.toLocaleDateString([], { month: 'long', year: 'numeric' })}`;
                  }
                } catch (err) {
                  console.error('Error formatting tooltip label:', err);
                  return label;
                }
              }}
            />
            
            {/* Single combined Legend for all items */}
            {renderLegend('Energy Usage')}
            
            {/* Operational hours highlighting - only for hourly frequency */}
            {showOperationalHours && (
              <>
                {/* Render reference areas for operational hours */}
                {referenceAreas.map((area, index) => (
                  <ReferenceArea
                    key={`op-hours-${index}`}
                    x1={area.x1}
                    x2={area.x2}
                    fill={alpha(theme.palette.success.main, 0.4)}
                    fillOpacity={0.7}
                  />
                ))}
              </>
            )}
            
            <Area 
              type="monotone" 
              dataKey="kwh" 
              name="Energy Usage"
              stroke={theme.palette.primary.main} 
              fillOpacity={1}
              fill="url(#colorEnergy)" 
              activeDot={{ r: 8 }}
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </>
    );
  };
  
  // Render compact operational hours schedule card
  const renderOperationalHoursCard = () => {
    if (!operationalHours) return null;
    
    return (
      <Card 
        sx={{ 
          mb: 3, 
          borderRadius: 3, 
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          transition: theme.transitions.create(['box-shadow', 'transform'], {
            duration: theme.transitions.duration.standard
          }),
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
            transform: 'translateY(-4px)',
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            Operational Hours
          </Typography>
          
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableBody>
                {Object.entries(operationalHours.schedule)
                  .sort((a, b) => parseInt(a[0]) - parseInt(b[0])) // Sort by day number
                  .map(([dayNum, schedule]) => (
                    <TableRow key={dayNum} sx={{ 
                      '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.grey[200], 0.3) },
                      borderLeft: 4, 
                      borderColor: schedule.isOpen ? 'success.light' : 'grey.300'
                    }}>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 600, width: '40%' }}>
                        {schedule.day}
                      </TableCell>
                      <TableCell align="right" sx={{ width: '60%' }}>
                        {schedule.isOpen ? (
                          <>
                            {schedule.start} - {schedule.end}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Closed
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };
  
  // Main render function
  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <>
          {/* Selector Controls */}
          <Card 
            sx={{ 
              mb: 3, 
              borderRadius: 3, 
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              transition: theme.transitions.create(['box-shadow', 'transform'], {
                duration: theme.transitions.duration.standard
              }),
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                transform: 'translateY(-4px)',
              }
            }}
          >
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                {/* Time Frame Selector */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel id="time-frame-select-label">Time Frame</InputLabel>
                    <Select
                      labelId="time-frame-select-label"
                      id="time-frame-select"
                      value={timeFrame}
                      onChange={handleTimeFrameChange}
                      label="Time Frame"
                      startAdornment={
                        timeFrame === 'Last Week' ? <DateRangeIcon sx={{ mr: 1 }} /> :
                        timeFrame === 'Last 30 Days' ? <EventIcon sx={{ mr: 1 }} /> :
                        timeFrame === 'Last Year' ? <CalendarTodayIcon sx={{ mr: 1 }} /> :
                        <AllInclusiveIcon sx={{ mr: 1 }} />
                      }
                    >
                      <MenuItem value="Last Week">Last Week</MenuItem>
                      <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
                      <MenuItem value="Last Year">Last Year</MenuItem>
                      <MenuItem value="All Time">All Time</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Frequency Selector - dynamically populated */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel id="frequency-select-label">Frequency</InputLabel>
                    <Select
                      labelId="frequency-select-label"
                      id="frequency-select"
                      value={frequency}
                      onChange={handleFrequencyChange}
                      label="Frequency"
                      startAdornment={
                        frequency === 'Hourly' ? <AccessTimeIcon sx={{ mr: 1 }} /> :
                        frequency === 'Daily' ? <TodayIcon sx={{ mr: 1 }} /> :
                        frequency === 'Weekly' ? <ViewWeekIcon sx={{ mr: 1 }} /> :
                        <CalendarMonthIcon sx={{ mr: 1 }} />
                      }
                    >
                      {getAvailableFrequencies(timeFrame).map(freq => (
                        <MenuItem key={freq} value={freq}>
                          {freq}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          {/* Chart Card */}
          <Card 
            sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              transition: theme.transitions.create(['box-shadow', 'transform'], {
                duration: theme.transitions.duration.standard
              }),
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                transform: 'translateY(-4px)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {renderChart()}
            </CardContent>
          </Card>
          
          {/* Operational Hours Card */}
          {renderOperationalHoursCard()}
        </>
      )}
    </Box>
  );
};

export default UsagePatterns; 